/**
 * In-memory cache for DB-backed committed PFM mindmaps (per run + scope + focus node).
 * Makes breadcrumb / drill-back navigation instantaneous after first load.
 */

import {
  clearPersistedCommittedMindmapScopeCacheForExecution,
  setPersistedCommittedMindmapScopeCache,
} from "./pfm-committed-mindmap-session-cache.ts";

export type CommittedMindmapClickableNode = {
  nodeKey?: string;
  node_key?: string;
  title?: string;
  label?: string;
  childCount?: number;
  child_count?: number;
};

export type CommittedMindmapScopeCacheEntry = {
  mermaid: string;
  svg: string;
  svgRaw: string;
  clickableNodes: CommittedMindmapClickableNode[];
  treeListPayload?: unknown;
  version: number;
  sourceReadId: string;
  sourceRunNumber: number;
  generatedAt: number;
};

const MAX_ENTRIES = 48;
const cache = new Map<string, CommittedMindmapScopeCacheEntry>();
const order: string[] = [];

function touch(key: string): void {
  const idx = order.indexOf(key);
  if (idx >= 0) order.splice(idx, 1);
  order.push(key);
  while (order.length > MAX_ENTRIES) {
    const drop = order.shift();
    if (drop) cache.delete(drop);
  }
}

export function getCommittedMindmapScopeCache(
  key: string,
): CommittedMindmapScopeCacheEntry | null {
  const k = String(key || "").trim();
  if (!k) return null;
  const hit = cache.get(k);
  if (hit) touch(k);
  return hit ?? null;
}

export function setCommittedMindmapScopeCache(
  key: string,
  entry: CommittedMindmapScopeCacheEntry,
): void {
  const k = String(key || "").trim();
  if (!k || !entry.svg) return;
  cache.set(k, entry);
  touch(k);
  setPersistedCommittedMindmapScopeCache(k, entry);
}

export function clearCommittedMindmapScopeCacheForExecution(executionId: string): void {
  const prefix = `${String(executionId || "").trim()}|`;
  if (!prefix || prefix === "|") return;
  for (const key of [...cache.keys()]) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
      const idx = order.indexOf(key);
      if (idx >= 0) order.splice(idx, 1);
    }
  }
  clearPersistedCommittedMindmapScopeCacheForExecution(executionId);
}
